import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../Layout/Layout';
import { subscriptionService } from '../../services/subscriptionService';
import { AuthContext } from '../../contexts/AuthContext';
import Dialog from '../common/Dialog';
import UpgradeBar from '../common/UpgradeBar';
import { Form, FormGroup, Label, Input, ButtonGroup, Button, Select } from '../common/Form';
import { SUBSCRIPTION_TAGS } from '../../data/constants';
import { tagService } from '../../services/tagService';

const Container = styled.div`
  padding: 24px;
  background: var(--background);
  min-height: 100vh;
  color: var(--text-primary);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 32px;
  color: var(--text-primary);
  font-weight: 600;
`;

const NewSubscriptionButton = styled.button`
  background: #4F46E5;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
`;

const FilterBar = styled.div`
  background: var(--surface);
  border-radius: 12px;
  padding: 12px;
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  border: 1px solid var(--border);
`;

const FilterOption = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  background: ${props => props.active ? 'var(--hover)' : 'transparent'};
  color: var(--text-primary);
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;

  &:hover {
    background: var(--hover);
  }
`;

const Badge = styled.span`
  background: ${props => props.active ? 'var(--primary)' : 'var(--surface-hover)'};
  color: ${props => props.active ? 'white' : 'var(--text-primary)'};
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
`;

const SearchContainer = styled.div`
  background: var(--surface);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid var(--border);
`;

const SearchWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 40px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 16px;
  background: var(--background);
  color: var(--text-primary);
  transition: all 0.2s ease;

  &::placeholder {
    color: var(--text-secondary);
    font-size: 14px;
  }

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px var(--primary-light);
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
  pointer-events: none;
`;

const ViewToggle = styled.button`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 8px 12px;
  color: var(--text-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: var(--hover);
  }
`;

const TableContainer = styled.div`
  background: var(--surface);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--border);
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 40px 1fr 1fr 1fr 1fr 1fr 1fr 1fr 40px;
  padding: 16px;
  border-bottom: 1px solid var(--border);
  color: var(--text-secondary);
  font-weight: 500;
  background: var(--surface);

  > div {
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;

    &:hover {
      color: var(--text-primary);
    }
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 30px 1fr 1fr 1fr 1fr 1fr 1fr 1fr 40px;
  padding: 16px;
  border-bottom: 1px solid var(--border);
  color: var(--text-primary);
  align-items: center;

  &:hover {
    background: var(--hover);
  }
`;

const CycleTag = styled.span`
  background: var(--tag-yellow);
  color: var(--tag-yellow-text);
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;

const Tag = styled.span`
  background: var(--surface-hover);
  color: var(--text-primary);
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
`;

const DueDate = styled.span`
  color: var(--text-secondary);
  font-size: 14px;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  color: #666;
  background: #1A1A1A;
  border-top: 1px solid #2F2F2F;
`;

const PerPageSelect = styled.select`
  background: #2F2F2F;
  color: #fff;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;

  &:focus {
    outline: none;
  }
`;

const UpgradeBarContainer = styled.div`
  background: linear-gradient(135deg, #1a365d 0%, #2563eb 100%);
  padding: 18px 28px;
  border-radius: 14px;
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
  box-shadow: var(--shadow-md);
  animation: slideIn 0.5s ease-out;

  @keyframes slideIn {
    from { transform: translateX(-20px); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
`;

const UpgradeLink = styled.button`
  color: white;
  background: rgba(255, 255, 255, 0.2);
  padding: 8px 16px;
  border-radius: 20px;
  border: none;
  cursor: pointer;
  font-weight: 500;
  transition: all var(--transition-normal);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
  }
`;

const FilterIcon = styled.button`
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--surface);
  color: var(--text-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: var(--hover);
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  text-align: center;
  gap: 24px;
  background: var(--surface);
  border-radius: 16px;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border);
  margin-top: 24px;
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  background: var(--gradient-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: white;
  margin-bottom: 16px;
  animation: pulse 2s infinite;

  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }
`;

const EmptyText = styled.div`
  color: var(--text-secondary);
  font-size: 18px;
  max-width: 400px;
  line-height: 1.6;
`;

const ColumnsDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  width: 200px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 4px 12px var(--card-shadow);
  margin-top: 8px;
  z-index: 1000;
  padding: 16px;
`;

const ColumnsTitle = styled.div`
  font-weight: 500;
  margin-bottom: 16px;
  color: var(--text-primary);
`;

const ColumnOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  cursor: pointer;
  color: var(--text-primary);

  &:hover {
    opacity: 0.8;
  }
`;

const Checkbox = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid ${props => props.checked ? 'var(--text-primary)' : 'var(--border)'};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.checked ? 'var(--text-primary)' : 'transparent'};
  color: var(--surface);
`;

const FiltersDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 600px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 4px 12px var(--card-shadow);
  margin-top: 8px;
  z-index: 1000;
  padding: 24px;
`;

const FiltersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const FiltersTitle = styled.h2`
  font-size: 20px;
  font-weight: 500;
`;

const FilterButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const FilterResetButton = styled.button`
  background: none;
  border: none;
  color: var(--error);
  cursor: pointer;
  font-size: 14px;
  padding: 4px 8px;

  &:hover {
    text-decoration: underline;
  }
`;

const FilterDoneButton = styled.button`
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 12px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    opacity: 0.9;
  }
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FilterLabel = styled.label`
  font-size: 16px;
  font-weight: 500;
`;

const FilterSelect = styled.select`
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--surface);
  color: var(--text-primary);
  font-size: 16px;
  width: 100%;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--text-primary);
  }
`;

const DateInput = styled.input`
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--surface);
  color: var(--text-primary);
  font-size: 16px;
  width: 100%;

  &:focus {
    outline: none;
    border-color: var(--text-primary);
  }
`;

const SubscriptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SubscriptionGroup = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border);
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border);
`;

const GroupTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const GroupCount = styled.span`
  background: #E5F0FF;
  color: #2563eb;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
`;

const SubscriptionCard = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border-bottom: 1px solid var(--border);
  transition: all 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f8f9fa;
  }
`;

const CompanyLogo = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  object-fit: contain;
`;

const SubscriptionInfo = styled.div`
  flex: 1;
`;

const CompanyName = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: #333;
  margin-bottom: 4px;
`;

const SubscriptionDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: #666;
  font-size: 14px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ActionIcon = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: var(--text-secondary);
  transition: all 0.2s;

  &:hover {
    color: ${props => props.delete ? 'var(--error)' : 'var(--primary)'};
    transform: translateY(-1px);
  }
`;

const DropdownIcon = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: var(--text-primary);
  }
`;

const DropdownContent = styled.div`
  display: ${props => props.isOpen ? 'grid' : 'none'};
  grid-template-columns: 30px 1fr 1fr 1fr 1fr 1fr 1fr 1fr 40px;
  padding: 16px;
  background: var(--surface-hover);
  border-bottom: 1px solid var(--border);
`;

const AlertButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #FEF3C7;
  color: #92400E;
  border: 1px solid #F59E0B;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-right: 12px;

  &:hover {
    background: #FDE68A;
  }
`;

const AddButton = styled.button`
  background: #4F46E5;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
`;

const PlusIcon = styled.span`
  width: 20px;
  height: 20px;
  background: url('/assets/icons/plus.svg') no-repeat center;
  background-size: contain;
  display: inline-block;
  margin-right: 8px;
`;

const Subscriptions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [activeFilter, setActiveFilter] = useState('all');
  const [subscriptionCount, setSubscriptionCount] = useState(0);
  const [subscriptions, setSubscriptions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
  const [showColumnsDropdown, setShowColumnsDropdown] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    provider: true,
    cycle: true,
    price: true,
    method: true,
    startDate: false,
    type: false,
    tags: true
  });
  const columnsRef = useRef(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    cycle: 'all',
    type: 'all',
    paymentMethod: 'all',
    tags: 'all',
    fromDate: '',
    toDate: ''
  });
  const filtersRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionsByType, setSubscriptionsByType] = useState({
    monthly: [],
    yearly: [],
    weekly: []
  });
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [subscriptionToDelete, setSubscriptionToDelete] = useState(null);
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [userTags, setUserTags] = useState([]);

  const loadSubscriptions = useCallback(async () => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      const data = await subscriptionService.getAllSubscriptions(user.id);
      setSubscriptions(data);
      setSubscriptionCount(data.length);
      
      // Update subscription types
      const grouped = data.reduce((acc, sub) => {
        const cycle = sub.cycle.toLowerCase();
        if (!acc[cycle]) acc[cycle] = [];
        acc[cycle].push(sub);
        return acc;
      }, { monthly: [], yearly: [], weekly: [] });
      
      setSubscriptionsByType(grouped);
    } catch (error) {
      console.error('Error loading subscriptions:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Load subscriptions on mount and when user changes
  useEffect(() => {
    loadSubscriptions();
  }, [loadSubscriptions]);

  // Refresh when navigating back from create/edit
  useEffect(() => {
    if (location.state?.refresh) {
      loadSubscriptions();
      // Clear the refresh flag
      navigate(location.pathname, { replace: true });
    }
  }, [location, loadSubscriptions, navigate]);

  const handleSubscriptionCreated = () => {
    loadSubscriptions();
    setShowSuccessDialog(true);
  };

  const filterOptions = [
    { id: 'all', label: 'All', icon: '📦' },
    { id: 'monthly', label: 'Monthly', icon: '📅' },
    { id: 'yearly', label: 'Yearly', icon: '📆' },
    { id: 'weekly', label: 'Weekly', icon: '📆' },
  ];

  const getFilterCount = (filterId) => {
    switch (filterId) {
      case 'all':
        return subscriptionCount;
      case 'monthly':
        return subscriptionsByType.monthly.length;
      case 'yearly':
        return subscriptionsByType.yearly.length;
      case 'weekly':
        return subscriptionsByType.weekly.length;
      default:
        return 0;
    }
  };

  const getFilteredSubscriptions = () => {
    let filtered = { ...subscriptionsByType };

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      Object.keys(filtered).forEach(type => {
        filtered[type] = filtered[type].filter(sub => {
          return (
            sub.name?.toLowerCase().includes(query) ||
            sub.provider?.toLowerCase().includes(query) ||
            sub.cycle?.toLowerCase().includes(query) ||
            sub.price?.toString().includes(query) ||
            sub.payment_method?.toLowerCase().includes(query) ||
            sub.start_date?.includes(query) ||
            sub.tags?.some(tag => tag.toLowerCase().includes(query))
          );
        });
      });
    }

    // Apply filters
    Object.keys(filtered).forEach(type => {
      filtered[type] = filtered[type].filter(sub => {
        // Cycle filter
        if (filters.cycle !== 'all' && sub.cycle.toLowerCase() !== filters.cycle) {
          return false;
        }

        // Payment Method filter
        if (filters.paymentMethod !== 'all' && 
            sub.payment_method.toLowerCase() !== filters.paymentMethod) {
          return false;
        }

        // Tags filter
        if (filters.tags !== 'all' && 
            !sub.tags?.some(tag => tag.toLowerCase() === filters.tags)) {
          return false;
        }

        // Date range filter
        if (filters.fromDate || filters.toDate) {
          const subDate = new Date(sub.start_date);
          if (filters.fromDate && new Date(filters.fromDate) > subDate) {
            return false;
          }
          if (filters.toDate && new Date(filters.toDate) < subDate) {
            return false;
          }
        }

        return true;
      });
    });

    return filtered;
  };

  const handleExport = () => {
    // Add export functionality
    alert('Export functionality will be added here');
  };

  const columnOptions = [
    { id: 'name', label: 'Name' },
    { id: 'provider', label: 'Provider' },
    { id: 'cycle', label: 'Cycle' },
    { id: 'price', label: 'Price' },
    { id: 'method', label: 'Method' },
    { id: 'startDate', label: 'Start date' },
    { id: 'type', label: 'Type' },
    { id: 'tags', label: 'Tags' }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnsRef.current && !columnsRef.current.contains(event.target)) {
        setShowColumnsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleColumn = (columnId) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnId]: !prev[columnId]
    }));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filtersRef.current && !filtersRef.current.contains(event.target)) {
        setShowFilters(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value.toLowerCase()
    }));
  };

  const resetFilters = () => {
    setFilters({
      cycle: 'all',
      type: 'all',
      paymentMethod: 'all',
      tags: 'all',
      fromDate: '',
      toDate: ''
    });
    setShowFilters(false); // Close the filters dropdown after resetting
  };

  const handleEdit = (subscription) => {
    navigate(`/subscriptions/edit/${subscription.id}`);
  };

  const handleDeleteClick = async (subscription) => {
    setSubscriptionToDelete(subscription);
    setShowConfirmDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!subscriptionToDelete) return;

    try {
      await subscriptionService.deleteSubscription(subscriptionToDelete.id);
      setShowConfirmDialog(false);
      // Refresh subscriptions immediately after deletion
      await loadSubscriptions();
      setShowSuccessDialog(true);
    } catch (error) {
      console.error('Error deleting subscription:', error);
      alert('Failed to delete subscription');
    }
  };

  const handleNewSubscription = () => {
    navigate('/subscriptions/create');
  };

  const toggleDropdown = (subscriptionId) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [subscriptionId]: !prev[subscriptionId]
    }));
  };

  useEffect(() => {
    const fetchUserTags = async () => {
      if (!user) return;
      
      try {
        const userCreatedTags = await tagService.getAllTags(user.id);
        
        const formattedUserTags = userCreatedTags.map(tag => ({
          value: tag.name.toLowerCase(),
          label: tag.name,
          isUserTag: true
        }));

        setUserTags(formattedUserTags);
      } catch (error) {
        console.error('Error fetching tags:', error);
        setUserTags([]);
      }
    };

    fetchUserTags();
  }, [user]);

  const handleAlertClick = () => {
    navigate('/subscriptions/alerts');
  };

  return (
    <Layout>
      <Container>
        <UpgradeBar />
        <Header>
          <Title>Subscriptions</Title>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <AlertButton onClick={handleAlertClick}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22ZM18 16V11C18 7.93 16.37 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.64 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16ZM16 17H8V11C8 8.52 9.51 6.5 12 6.5C14.49 6.5 16 8.52 16 11V17Z" fill="currentColor"/>
              </svg>
              Alerts
            </AlertButton>
            <AddButton onClick={() => navigate('/subscriptions/create')}>
              <PlusIcon /> New Subscription
            </AddButton>
          </div>
        </Header>

        <FilterBar>
          {filterOptions.map(option => (
            <FilterOption
              key={option.id}
              active={activeFilter === option.id}
              onClick={() => setActiveFilter(option.id)}
            >
              <span>{option.icon}</span>
              {option.label}
              <Badge active={activeFilter === option.id}>
                {getFilterCount(option.id)}
              </Badge>
            </FilterOption>
          ))}
        </FilterBar>

        <SearchContainer>
          <SearchWrapper>
            <SearchInput
              type="text"
              placeholder="Search by name, provider, price, cycle, payment method, date, or tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <SearchIcon>🔍</SearchIcon>

            <div ref={filtersRef} style={{ position: 'relative' }}>
              <FilterIcon onClick={() => setShowFilters(!showFilters)}>
                <span>🔽</span>
              </FilterIcon>
              
              {showFilters && (
                <FiltersDropdown>
                  <FiltersHeader>
                    <FiltersTitle>Filters</FiltersTitle>
                    <FilterButtonGroup>
                      <FilterResetButton onClick={resetFilters}>Reset</FilterResetButton>
                      <FilterDoneButton onClick={() => setShowFilters(false)}>Done</FilterDoneButton>
                    </FilterButtonGroup>
                  </FiltersHeader>
                  
                  <FiltersGrid>
                    <FilterGroup>
                      <FilterLabel>Cycle</FilterLabel>
                      <FilterSelect
                        value={filters.cycle}
                        onChange={(e) => handleFilterChange('cycle', e.target.value)}
                      >
                        <option value="all">All</option>
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                        <option value="weekly">Weekly</option>
                      </FilterSelect>
                    </FilterGroup>

                    <FilterGroup>
                      <FilterLabel>Payment Method</FilterLabel>
                      <FilterSelect
                        value={filters.paymentMethod}
                        onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
                      >
                        <option value="all">All</option>
                        <option value="credit card">Credit Card</option>
                        <option value="debit card">Debit Card</option>
                        <option value="paypal">PayPal</option>
                        <option value="bank transfer">Bank Transfer</option>
                      </FilterSelect>
                    </FilterGroup>

                    <FilterGroup>
                      <FilterLabel>Tags</FilterLabel>
                      <FilterSelect
                        value={filters.tags}
                        onChange={(e) => handleFilterChange('tags', e.target.value)}
                      >
                        <option value="all">All Tags</option>
                        <optgroup label="Your Tags">
                          {userTags
                            .filter(tag => tag.isUserTag)
                            .map(tag => (
                              <option key={tag.value} value={tag.value}>
                                {tag.label}
                              </option>
                            ))}
                        </optgroup>
                        <optgroup label="Default Tags">
                          {SUBSCRIPTION_TAGS.map(tag => (
                            <option key={tag.value} value={tag.value}>
                              {tag.label}
                            </option>
                          ))}
                        </optgroup>
                      </FilterSelect>
                    </FilterGroup>

                    <FilterGroup>
                      <FilterLabel>To Date</FilterLabel>
                      <DateInput
                        type="date"
                        value={filters.toDate}
                        onChange={(e) => handleFilterChange('toDate', e.target.value)}
                      />
                    </FilterGroup>
                  </FiltersGrid>
                </FiltersDropdown>
              )}
            </div>
          </SearchWrapper>
        </SearchContainer>

        <SubscriptionList>
          {Object.entries(getFilteredSubscriptions()).map(([cycle, subs]) => (
            subs.length > 0 && (
              <SubscriptionGroup key={cycle}>
                <GroupHeader>
                  <GroupTitle>
                    {cycle.charAt(0).toUpperCase() + cycle.slice(1)} Subscriptions
                  </GroupTitle>
                  <GroupCount>{subs.length}</GroupCount>
                </GroupHeader>
                <TableContainer>
                  <TableHeader>
                    <div></div>
                    <div>Name</div>
                    <div>Provider</div>
                    <div>Cycle</div>
                    <div>Price</div>
                    <div>Method</div>
                    <div>Start Date</div>
                    <div>Tags</div>
                    <div></div>
                  </TableHeader>

                  {subs.map(subscription => (
                    <TableRow key={subscription.id}>
                      <div></div>
                      <div>{subscription.name}</div>
                      <div>{subscription.provider}</div>
                      <div>
                        <CycleTag>{subscription.cycle}</CycleTag>
                      </div>
                      <div>${subscription.price}</div>
                      <div>{subscription.payment_method}</div>
                      <div>
                        <DueDate>
                          {new Date(subscription.start_date).toLocaleDateString()}
                        </DueDate>
                      </div>
                      <div>
                        {subscription.tags?.map(tag => (
                          <Tag key={tag}>{tag}</Tag>
                        ))}
                      </div>
                      <ActionButtons>
                        <ActionIcon onClick={() => handleEdit(subscription)}>
                          ✏️
                        </ActionIcon>
                        <ActionIcon delete onClick={() => handleDeleteClick(subscription)}>
                          🗑️
                        </ActionIcon>
                      </ActionButtons>
                    </TableRow>
                  ))}
                </TableContainer>
              </SubscriptionGroup>
            )
          ))}
        </SubscriptionList>

        {Object.values(getFilteredSubscriptions()).every(subs => subs.length === 0) && (
          <EmptyState>
            <EmptyIcon>❌</EmptyIcon>
            <EmptyText>No Subscriptions Found</EmptyText>
            <EmptyText>
              {activeFilter === 'all' 
                ? 'Create a Subscription to get started.'
                : `No ${activeFilter} subscriptions found.`}
            </EmptyText>
            <NewSubscriptionButton onClick={handleNewSubscription}>
              New Subscription
            </NewSubscriptionButton>
          </EmptyState>
        )}

        {/* Confirm Delete Dialog */}
        <Dialog
          isOpen={showConfirmDialog}
          onClose={() => setShowConfirmDialog(false)}
          title="Confirm Delete"
          message={`Are you sure you want to delete "${subscriptionToDelete?.name}"?`}
          icon="🗑️"
          buttons={[
            {
              text: 'Delete',
              onClick: handleConfirmDelete,
              variant: 'danger'
            },
            {
              text: 'Cancel',
              onClick: () => setShowConfirmDialog(false),
              variant: 'secondary'
            }
          ]}
        />

        {/* Success Dialog */}
        <Dialog
          isOpen={showSuccessDialog}
          onClose={() => setShowSuccessDialog(false)}
          title="Success!"
          message={successMessage}
          icon="✅"
        />

       
      </Container>
    </Layout>
  );
};

export default Subscriptions;